// ===========================================
// Makeup Eligibilities Card Component
// ===========================================
// Displays unredeemed makeup eligibilities for admin dashboard
// Allows quick view of students with pending makeup lessons

import {
  Box,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
  Typography,
  Skeleton,
  Chip,
  Button,
  Divider,
} from '@mui/material';
import {
  Autorenew as MakeupIcon,
  MusicNote as InstrumentIcon,
  Person as StudentIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { formatDistanceToNow, format } from 'date-fns';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { MakeupEligibility } from '../../api/booking.api';

// ===========================================
// TYPES
// ===========================================

interface MakeupEligibilitiesCardProps {
  eligibilities: MakeupEligibility[];
  isLoading: boolean;
  maxItems?: number;
}

// ===========================================
// HELPER FUNCTIONS
// ===========================================

const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function formatTime(time: string): string {
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
}

// ===========================================
// COMPONENT
// ===========================================

export default function MakeupEligibilitiesCard({
  eligibilities,
  isLoading,
  maxItems = 5,
}: MakeupEligibilitiesCardProps) {
  const navigate = useNavigate();
  const { schoolSlug } = useParams<{ schoolSlug: string }>();

  // Filter to only unredeemed eligibilities
  const unredeemedEligibilities = eligibilities.filter((e) => !e.redeemedAt);
  const displayedEligibilities = unredeemedEligibilities.slice(0, maxItems);
  const remainingCount = unredeemedEligibilities.length - maxItems;

  // Handle click on student - navigate to student edit page
  const handleStudentClick = (studentId: string) => {
    navigate(`/${schoolSlug}/admin/students?edit=${studentId}`);
  };

  // Loading state
  if (isLoading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MakeupIcon color="primary" />
              <Typography variant="h6">Make-up Eligibilities</Typography>
            </Box>
          }
        />
        <Divider sx={{ mx: 2 }} />
        <CardContent>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" height={60} sx={{ mb: 1, borderRadius: 1 }} />
          ))}
        </CardContent>
      </Card>
    );
  }

  // Empty state
  if (unredeemedEligibilities.length === 0) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MakeupIcon color="action" />
              <Typography variant="h6">Make-up Eligibilities</Typography>
            </Box>
          }
        />
        <Divider sx={{ mx: 2 }} />
        <CardContent>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <MakeupIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">
              No pending make-up lessons
            </Typography>
            <Typography variant="caption" color="text.disabled">
              Eligibilities are created when students have excused absences
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MakeupIcon color="warning" />
            <Typography variant="h6">Make-up Eligibilities</Typography>
          </Box>
        }
        action={
          <Chip
            label={`${unredeemedEligibilities.length} pending`}
            size="small"
            color="warning"
            sx={{ mt: 0.5 }}
          />
        }
        sx={{ pb: 0 }}
      />
      <Divider sx={{ mx: 2, mt: 2 }} />
      <CardContent sx={{ p: 0 }}>
        <List disablePadding>
          {displayedEligibilities.map((eligibility, index) => {
            const timeAgo = formatDistanceToNow(new Date(eligibility.grantedAt), { addSuffix: true });

            return (
              <ListItem
                key={eligibility.id}
                divider={index < displayedEligibilities.length - 1}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                  py: 1.5,
                  px: 2,
                }}
                onClick={() => handleStudentClick(eligibility.studentId)}
              >
                <ListItemText
                  primaryTypographyProps={{ component: 'div' }}
                  secondaryTypographyProps={{ component: 'div' }}
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <StudentIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                        <Typography variant="body2" fontWeight="medium">
                          {eligibility.Student.firstName} {eligibility.Student.lastName}
                        </Typography>
                      </Box>
                      {eligibility.adminOffered && (
                        <Chip
                          size="small"
                          label="Sent to family"
                          sx={{
                            height: 20,
                            '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' },
                            bgcolor: 'info.light',
                            color: 'info.contrastText',
                          }}
                        />
                      )}
                      {eligibility.Instrument && (
                        <Chip
                          size="small"
                          label={eligibility.Instrument.name}
                          icon={<InstrumentIcon sx={{ fontSize: 14 }} />}
                          sx={{
                            height: 20,
                            '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' },
                            '& .MuiChip-icon': { fontSize: 14, ml: 0.5, color: 'white' },
                            bgcolor: eligibility.Instrument.color || 'primary.main',
                            color: 'white',
                          }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        {eligibility.Lesson ? (
                          <>
                            <Typography variant="caption" color="text.secondary">
                              Missed: {eligibility.Lesson.name}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <ScheduleIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                              <Typography variant="caption" color="text.disabled">
                                {dayNames[eligibility.Lesson.dayOfWeek]} {formatTime(eligibility.Lesson.startTime)}
                              </Typography>
                            </Box>
                          </>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Credit available to use
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
                        {eligibility.Attendance && (
                          <>
                            <Typography variant="caption" color="text.disabled">
                              {format(new Date(eligibility.Attendance.date), 'd MMM yyyy')}
                            </Typography>
                            {eligibility.Attendance.absenceReason && (
                              <Typography
                                variant="caption"
                                color="text.disabled"
                                sx={{
                                  fontStyle: 'italic',
                                  maxWidth: 150,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                ({eligibility.Attendance.absenceReason})
                              </Typography>
                            )}
                          </>
                        )}
                        <Typography variant="caption" color="warning.main">
                          Granted {timeAgo}
                        </Typography>
                      </Box>
                    </Box>
                  }
                />
              </ListItem>
            );
          })}
        </List>

        {/* Show more link if there are more eligibilities */}
        {remainingCount > 0 && (
          <Box sx={{ p: 2, pt: 1, textAlign: 'center' }}>
            <Button
              size="small"
              component={Link}
              to={`/${schoolSlug}/admin/students?filter=makeup`}
              sx={{ textTransform: 'none' }}
            >
              View all ({unredeemedEligibilities.length} students with pending make-ups)
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
